import { http } from '@/lib/http'
import type { Subscription, SubscriptionPendingStatus, SubscriptionPlan, SubscriptionUpgradeResponse, SubscriptionValidateResponse } from '@/types/models'

/** The plan model is `omitempty`, so a zero is missing from the JSON, not 0; default it here so nothing does arithmetic on `undefined`. */
function withNumberDefaults(plan: SubscriptionPlan): SubscriptionPlan {
  return {
    ...plan,
    rank: plan.rank ?? 0,
    price: plan.price ?? 0,
    leaseFeeBase: plan.leaseFeeBase ?? 0,
    leaseFeeStepDownPct: plan.leaseFeeStepDownPct ?? 0,
    leaseFeeFloorPct: plan.leaseFeeFloorPct ?? 0,
  }
}

/** The checkouts this client can open; the backend sends nothing else (LP-514). */
export const PAYMENT_PROVIDERS = ['razorpay', 'paddle']
/** One-off charges — the lease fee and agreement charges — also open Cashfree (LP-521); plans never go through it. */
export const ORDER_PAYMENT_PROVIDERS = [...PAYMENT_PROVIDERS, 'cashfree']

export const subscriptionApi = {
  plans: () => http.get<SubscriptionPlan[] | null>('/subscription-plans').then((d) => (d ?? []).map(withNumberDefaults)),
  /** The caller's active plan. 404 "active subscription not found" is the normal answer for tenants. */
  active: () => http.get<Subscription>('/v2/subscription'),
  pendingStatus: (subscriptionId: string) => http.get<SubscriptionPendingStatus>(`/v2/subscription-status/${subscriptionId}`),
  /** A free plan activates at once (no `key`). This client opens Razorpay and Paddle checkouts (LP-514). */
  /** `promoCode` (LP-1002): a code that can't be used refuses the checkout with 409 and the code's message. */
  upgrade: (planId: string, promoCode?: string) =>
    http.post<SubscriptionUpgradeResponse>('/v2/subscription', promoCode ? { planId, promoCode, paymentProviders: PAYMENT_PROVIDERS } : { planId, paymentProviders: PAYMENT_PROVIDERS }),
  /** A short-lived link to Paddle's customer portal for a Paddle subscriber: card, cancellation, invoices. */
  portal: () => http.post<{ url: string }>('/v2/subscription/portal', {}),
  /** Where the pending change stands; after 8 minutes the backend settles it one way or the other. */
  validate: () => http.get<SubscriptionValidateResponse>('/v2/subscription-validate'),
  cancelPending: () => http.post<null>('/v2/subscription-cancel', {}),
}
